import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup"
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build/build/ckeditor";
import apiHelper from "../../../../util/ApiHelper";
import { toast } from "react-toastify";
import editorConfiguration from "../../../../util/EditorConfiguration";
import { useLoading } from "../../../../util/LoadingProvider";


const schema = yup.object().shape({
  name: yup.string(),
  valueEn: yup.string(),
  valueTc: yup.string(),
});

const defaultFormValues = {
  pageTitleTc: "",
  pageTitleEn: "",
  subtitleTc: "",
  subtitleEn: "",
  highlightsTc: "",
  highlightsEn: ""
};

const useStyles = makeStyles((theme) => ({
  textInput: {
    width: "100%",
    marginBottom: "12px",

    "& input": {
      fontSize: "14px",
    },
  },
  content: {
    "& .ck-editor__editable_inline": {
      minHeight: "362px",
    },
  },
  buttonGroup: {
    marginTop: "40px",
    marginRight: "15px",
    textAlign: "right",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
  box: {
    padding: 0,
  },
  input: {
    width: "100%",
  },
}));

export default function LogoGroupGeneralSettingPage({ disabled, ...props }) {
  const { match } = props;
  const id = match.params.id;
  const [defaultValues, setDefaultValues] = useState(defaultFormValues);
  const [resultValues, setResultValues] = React.useState(null);
  const { setLoading } = useLoading();

  const classes = useStyles();

  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    reset,
  } = useForm({
    defaultValues: React.useMemo(() => defaultValues, [defaultValues]),
    resolver: yupResolver(schema),
  });

  const onSubmit = (value) => {
    updateLogoGroupGeneralSetting(value);
  };

  const getLogoGroupGeneralSetting = React.useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiHelper.hkiff.getLogoPage();
      console.log(result);
      const data = result.data;
      setDefaultValues(data);
      reset(data);
    } catch (err) {
      console.log(err);
    }
    setLoading(false);
  }, [setLoading]);

  const updateLogoGroupGeneralSetting = async (value) => {
    try {
      const result = await apiHelper.hkiff.updateLogoPage(value);
      console.log(result);
      if (result.status === 200) {
        toast.dark("Your changes are saved.");
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    getLogoGroupGeneralSetting();
  }, [getLogoGroupGeneralSetting]);

  return (
    <div className={classes.content}>
      <Box width="100%">
        <Typography variant="h4" gutterBottom>
           Logo Group > General Settings
        </Typography>
      </Box>
      <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
        <Grid container xs={12} direction="row" spacing={3}>
          <Grid item xs={12}>
            <Controller
              name="pageTitleEn"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  label="Title (English)"
                  variant="outlined"
                  margin="dense"
                  className={classes.textInput}
                  fullWidth
                  {...field}
                  error={!!error}
                  helperText={error ? error.message : ""}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="pageTitleTc"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  label="Title (Traditional Chinese)"
                  variant="outlined"
                  margin="dense"
                  className={classes.textInput}
                  fullWidth
                  {...field}
                  error={!!error}
                  helperText={error ? error.message : ""}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="subtitleEn"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  label="Subtitle (English)"
                  variant="outlined"
                  margin="dense"
                  className={classes.textInput}
                  fullWidth
                  {...field}
                  error={!!error}
                  helperText={error ? error.message : ""}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="subtitleTc"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  label="Subtitle (Traditional Chinese)"
                  variant="outlined"
                  margin="dense"
                  className={classes.textInput}
                  fullWidth
                  {...field}
                  error={!!error}
                  helperText={error ? error.message : ""}
                />
              )}
            />
          </Grid>
        </Grid>

        {defaultValues && (
          <>
            <Box
              fontWeight={900}
              fontSize={13}
              mb={4}
              style={{ marginTop: "30px", marginBottom: "10px" }}
              width="100%"
            >
              <Typography variant="p">Content (English)</Typography>
            </Box>

            <Grid container xs={12} direction="row" spacing={3}>
              <Grid item xs={12}>
                <Controller
                  name="highlightsEn"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <CKEditor
                      editor={Editor}
                      config={editorConfiguration}
                      data={field.value}
                      disabled={disabled}
                      onReady={(editor) => {
                        // You can store the "editor" and use when it is needed.
                        console.log("Editor is ready to use!", editor);
                        editor.setData(defaultValues.highlightsEn);
                      }}
                      onChange={(event, editor) => {
                        const data = editor.getData();
                        field.onChange(data);
                        // console.log({ event, editor, data });
                      }}
                      onBlur={(event, editor) => {
                        console.log("Blur.", editor);
                      }}
                      onFocus={(event, editor) => {
                        console.log("Focus.", editor);
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Box fontWeight={900} fontSize={13} mt={4} mb={4} width="100%" style={{ marginTop: "30px", marginBottom: "10px" }}>
              <Typography variant="p">Content (Traditional Chinese)</Typography>
            </Box>

            <Grid container xs={12} direction="row" spacing={3}>
              <Grid item xs={12}>
                <Controller
                  name="highlightsTc"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <CKEditor
                      editor={Editor}
                      config={editorConfiguration}
                      data={field.value}
                      disabled={disabled}
                      onReady={(editor) => {
                        // You can store the "editor" and use when it is needed.
                        console.log("Editor is ready to use!", editor);
                        editor.setData(defaultValues.highlightsTc);
                      }}
                      onChange={(event, editor) => {
                        const data = editor.getData();
                        field.onChange(data);
                        // console.log({ event, editor, data });
                      }}
                      onBlur={(event, editor) => {
                        console.log("Blur.", editor);
                      }}
                      onFocus={(event, editor) => {
                        console.log("Focus.", editor);
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </>
        )}
        


        {disabled || (
          <div className={classes.buttonGroup}>
            <Button
              type="submit"
              variant="contained"
              disableElevation
              style={{
                color: "white",
                background: "#306F9D",
                marginRight: "5px",
                padding: "9px 16px",
              }}
            >
              Save
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
